import { useState, useCallback } from 'react';
import { apiService, ApiError } from '../services/apiService';

export interface UseApiReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  execute: (...args: any[]) => Promise<T | null>;
  clearError: () => void;
}

/**
 * Generic hook for API calls with authentication
 */
export const useApi = <T = any>(): UseApiReturn<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (apiCall: () => Promise<T>): Promise<T | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiCall();
      setData(result);
      return result;
    } catch (err: any) {
      let errorMessage = 'An error occurred';
      
      if (err instanceof ApiError) {
        errorMessage = `API Error (${err.status}): ${err.message}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('API call error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    data,
    isLoading,
    error,
    execute,
    clearError,
  };
};

/**
 * Specific hook for GET requests
 */
export const useApiGet = <T = any>(endpoint: string, scopes?: string[]): UseApiReturn<T> & {
  refetch: () => Promise<T | null>;
} => {
  const api = useApi<T>();

  const refetch = useCallback(async () => {
    return api.execute(() => apiService.get<T>(endpoint, scopes));
  }, [endpoint, scopes, api.execute]);

  return {
    ...api,
    refetch,
  };
};

/**
 * Specific hook for POST requests
 */
export const useApiPost = <T = any>(endpoint: string, scopes?: string[]): UseApiReturn<T> & {
  post: (data?: any) => Promise<T | null>;
} => {
  const api = useApi<T>();

  const post = useCallback(async (data?: any) => {
    return api.execute(() => apiService.post<T>(endpoint, data, scopes));
  }, [endpoint, scopes, api.execute]);

  return {
    ...api,
    post,
  };
};